import React from 'react';
import { Typography, Card as AntCard } from 'antd';
import { Target, Heart, Lightbulb, Users, TrendingUp, Leaf } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

// Type workaround for Card component
const Card = AntCard as any;

const { Title, Text, Paragraph } = Typography;

export const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Helmet>
        <title>About QodeAris - Agritech Innovation for India</title>
        <meta name="description" content="QodeAris is an agritech innovation company building affordable, scalable solutions like DhartiNetra for Indian farmers. Learn about our vision and team." />
        <meta name="keywords" content="about qodearis, agritech company, indian agriculture, smart farming team" />
      </Helmet>
      <Navbar />
      <main id="main-content" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Title level={1} className="text-5xl md:text-6xl font-bold mb-6">
              About
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"> QodeAris</span>
            </Title>
            <Paragraph className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              An agritech innovation company focused on building affordable, scalable, and impact-driven 
              solutions for Indian agriculture.
            </Paragraph>
          </motion.div>

          {/* Vision Section */}
          <section className="mb-16">
            <Card className="bg-gradient-to-r from-green-600 to-emerald-600 text-white border-0 shadow-xl">
              <div className="text-center py-8">
                <Target className="w-16 h-16 mx-auto mb-4" aria-hidden="true" />
                <Title level={2} className="text-white mb-4">Our Vision</Title>
                <Paragraph className="text-xl text-white/90 max-w-3xl mx-auto">
                  To make advanced agricultural technology accessible and affordable for every Indian farmer, 
                  helping them increase crop yield, reduce risk, and improve livelihoods.
                </Paragraph>
              </div>
            </Card>
          </section>

          {/* Mission & Values */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow">
                  <Heart className="w-12 h-12 text-red-600 mb-4" aria-hidden="true" />
                  <Title level={3} className="mb-3">Empathy-Driven</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Built with deep empathy for farmers and understanding of their real challenges. 
                    Every feature is designed from the grassroots perspective.
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow">
                  <Lightbulb className="w-12 h-12 text-yellow-600 mb-4" aria-hidden="true" />
                  <Title level={3} className="mb-3">Innovation</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Combining cutting-edge AI, IoT, and agricultural science to create solutions 
                    that are both advanced and practical.
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow">
                  <Users className="w-12 h-12 text-blue-600 mb-4" aria-hidden="true" />
                  <Title level={3} className="mb-3">Accessibility</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Making technology affordable and easy to use for smallholder farmers, 
                    with multi-language support and simple interfaces.
                  </Text>
                </Card>
              </motion.div>
            </div>
          </section>

          {/* Impact Section */}
          <section className="mb-16 bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 md:p-12">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Impact Created by DhartiNetra</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Our flagship product is making a real difference in farmers' lives
              </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-400" aria-hidden="true" />
                </div>
                <Title level={3} className="mb-3">Economic Impact</Title>
                <ul className="text-left space-y-2 text-gray-600 dark:text-gray-300">
                  <li>✓ Higher farmer income</li>
                  <li>✓ Reduced dependency on guesswork</li>
                  <li>✓ Stronger rural economies</li>
                  <li>✓ Optimized resource usage</li>
                </ul>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                </div>
                <Title level={3} className="mb-3">Social Impact</Title>
                <ul className="text-left space-y-2 text-gray-600 dark:text-gray-300">
                  <li>✓ Improved food security</li>
                  <li>✓ Knowledge sharing among communities</li>
                  <li>✓ Farmer empowerment</li>
                  <li>✓ Skill development</li>
                </ul>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Leaf className="w-8 h-8 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                </div>
                <Title level={3} className="mb-3">Environmental Impact</Title>
                <ul className="text-left space-y-2 text-gray-600 dark:text-gray-300">
                  <li>✓ Water conservation</li>
                  <li>✓ Reduced chemical usage</li>
                  <li>✓ Soil health preservation</li>
                  <li>✓ Sustainable farming practices</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Our Product */}
          <section className="mb-16">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-12 shadow-xl">
              <Title level={2} className="text-center mb-6">DhartiNetra – Our Flagship Product</Title>
              <Paragraph className="text-lg text-gray-600 dark:text-gray-300 text-center max-w-3xl mx-auto mb-8">
                DhartiNetra is our flagship and only product—built with deep empathy for farmers 
                and strong engineering excellence. It represents our commitment to creating technology 
                that truly serves the grassroots.
              </Paragraph>
              <div className="text-center">
                <Text className="text-xl font-semibold text-green-700 dark:text-green-400">
                  Not just a product—it's a movement to protect farmers, food systems, and the environment.
                </Text>
              </div>
            </div>
          </section>

          {/* Partnership Opportunities */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Who Can Partner With Us?</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                We collaborate with organizations that share our vision
              </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                'Farmer Producer Organizations (FPOs)',
                'Agri-input companies',
                'NGOs & CSR programs',
                'Government & Smart Village initiatives',
                'Agribusiness enterprises',
                'Agricultural cooperatives'
              ].map((partner, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="text-center shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-600 border-0">
                    <Text className="text-lg font-semibold text-gray-800 dark:text-white">
                      {partner}
                    </Text>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Our Journey */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Our Journey</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Building technology that serves the grassroots
              </Text>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {[
                  { year: '2024', title: 'Foundation', desc: 'QodeAris founded with a mission to democratize agricultural technology' },
                  { year: '2025', title: 'DhartiNetra Launch', desc: 'Launched our flagship product - DhartiNetra Farm Rakshak' },
                  { year: '2026', title: 'Expansion', desc: 'Growing our reach to serve more farmers across India' },
                ].map((milestone, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex gap-6 items-start"
                  >
                    <div className="flex-shrink-0 w-20 h-20 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      {milestone.year}
                    </div>
                    <div className="flex-1 pt-2">
                      <Title level={4} className="mb-2">{milestone.title}</Title>
                      <Text className="text-gray-600 dark:text-gray-300">{milestone.desc}</Text>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Team Section */}
          <section className="mb-16 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-600 rounded-2xl p-8 md:p-12">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Our Team</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Passionate technologists and agriculture experts working together
              </Text>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { role: 'Engineering', icon: '⚙️', desc: 'Building robust IoT and AI solutions' },
                { role: 'Agriculture', icon: '🌱', desc: 'Deep understanding of farming practices' },
                { role: 'Support', icon: '💬', desc: 'Dedicated to farmer success' },
              ].map((team, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center"
                >
                  <div className="text-5xl mb-4">{team.icon}</div>
                  <Title level={4} className="mb-2">{team.role}</Title>
                  <Text className="text-gray-600 dark:text-gray-300">{team.desc}</Text>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Pilot Program */}
          <section>
            <Card className="bg-gradient-to-r from-green-600 to-emerald-600 text-white border-0 shadow-xl">
              <div className="text-center py-8">
                <Title level={2} className="text-white mb-4">Start with a Pilot Program</Title>
                <Paragraph className="text-xl text-white/90 max-w-3xl mx-auto mb-6">
                  Begin with a small-scale pilot and see DhartiNetra's impact on yield, cost, and farmer 
                  confidence—before scaling across regions.
                </Paragraph>
                <div className="flex gap-4 justify-center flex-wrap">
                  <a href="/contact">
                    <button className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                      Contact Us
                    </button>
                  </a>
                  <a href="/products/dhartinetra">
                    <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors">
                      View Products
                    </button>
                  </a>
                </div>
              </div>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};
